import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays, FileText, Inbox, PackageOpen, RefreshCw, Settings2, Workflow } from 'lucide-react';
import { api } from '@/lib/api';
import { useSessao } from '@/hooks/useSessao';
import { Botao } from '@/componentes/ui/botao';
import { Segmentado } from '@/componentes/ui/segmentado';
import { Esteira } from '@/componentes/entradas/Esteira';
import { GavetaNota } from '@/componentes/entradas/GavetaNota';
import { GavetaAgendar } from '@/componentes/entradas/GavetaAgendar';
import { Grade } from '@/componentes/entradas/Grade';
import { Cadastro } from '@/componentes/entradas/Cadastro';
import { NfCompra } from '@/componentes/entradas/NfCompra';
import { MontarCarga } from '@/componentes/entradas/MontarCarga';
import { MeuSlack, RETORNO_SLACK } from '@/componentes/entradas/MeuSlack';

/**
 * Entradas — a NF de compra da SEFAZ ao estoque (MEG-868).
 *
 * Proposta do PH (01/10/2026): a nota vira um objeto com status que anda
 * sozinho, e o fiscal passa a auditar exceções. Esta é a tela de todo mundo
 * que mexe na nota — comprador, fiscal, logística, CD —, e por isso ela abre
 * no que é de quem entra, não em tudo.
 *
 * Quatro abas: a esteira (o trabalho), a grade de recebimento (o Excel de hoje),
 * montar carga (várias NFs de compra → vários destinos, sobre o saldo)
 * e o cadastro (só para quem tem `entradas.cadastro`). A nota abre numa gaveta
 * e o endereço guarda qual (`?nota=`) — é o link que vai no Slack.
 */

type Aba = 'esteira' | 'grade' | 'carga' | 'nfcompra' | 'cadastro';

export function Entradas(): JSX.Element {
  const { temPermissao } = useSessao();
  const cliente = useQueryClient();
  const [params, definirParams] = useSearchParams();
  const aba = (params.get('aba') as Aba | null) ?? 'esteira';
  const nota = params.get('nota');
  const [agendar, definirAgendar] = React.useState<string[]>([]);

  const mudar = (chave: string, valor: string | null): void => {
    const novo = new URLSearchParams(params);
    if (valor) novo.set(chave, valor);
    else novo.delete(chave);
    definirParams(novo, { replace: true });
  };

  const rodar = useMutation({
    mutationFn: () => api.post<{ resultado: string }>('/api/entradas/rodar'),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['entradas'] }),
  });

  const opcoes = [
    { valor: 'esteira' as const, rotulo: 'Esteira', icone: <Workflow className="h-4 w-4" /> },
    { valor: 'grade' as const, rotulo: 'Grade de recebimento', icone: <CalendarDays className="h-4 w-4" /> },
    { valor: 'carga' as const, rotulo: 'Montar carga', icone: <PackageOpen className="h-4 w-4" /> },
    { valor: 'nfcompra' as const, rotulo: 'NF de compra', icone: <FileText className="h-4 w-4" /> },
    ...(temPermissao('entradas.cadastro') ? [{ valor: 'cadastro' as const, rotulo: 'Cadastro', icone: <Settings2 className="h-4 w-4" /> }] : []),
  ];

  return (
    <div className="space-y-3">
      {/* Cabeçalho compacto (Paulo, 01/11/2026): título e abas na mesma linha; a descrição fica na dica do título. */}
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <h1
            className="flex items-center gap-2 text-lg font-bold leading-tight tracking-[-0.02em] text-texto"
            title="A nota de compra da SEFAZ ao estoque. Cada etapa tem um dono: comprador confere a OC, fiscal dá a entrada, logística agenda e o CD recebe."
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primaria-suave text-primaria ring-1 ring-inset ring-primaria/15">
              <Inbox className="h-4 w-4" />
            </span>
            Entradas
          </h1>
          <Segmentado rotuloAcessivel="Visão" valor={aba} opcoes={opcoes} aoMudar={(v) => mudar('aba', v === 'esteira' ? null : v)} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MeuSlack />
          {temPermissao('entradas.fiscal') ? (
            <Botao variante="secundaria" tamanho="pequeno" carregando={rodar.isPending} onClick={() => rodar.mutate()} title={rodar.data?.resultado}>
              <RefreshCw className="h-4 w-4" /> Atualizar agora
            </Botao>
          ) : null}
        </div>
      </header>
      {rodar.data ? <p className="text-xs text-textoFraco">{rodar.data.resultado}</p> : null}
      {params.get('slack') && RETORNO_SLACK[params.get('slack')!] ? (
        <p
          className={
            RETORNO_SLACK[params.get('slack')!]!.ok
              ? 'rounded-lg bg-status-livreFundo px-3 py-2 text-[13px] text-status-livre'
              : 'rounded-lg bg-status-atencaoFundo px-3 py-2 text-[13px] text-status-atencao'
          }
        >
          {RETORNO_SLACK[params.get('slack')!]!.texto}{' '}
          <button type="button" className="ml-1 underline" onClick={() => mudar('slack', null)}>
            ok
          </button>
        </p>
      ) : null}

      {aba === 'esteira' ? <Esteira aoAbrir={(c) => mudar('nota', c)} aoAgendarLote={definirAgendar} /> : null}
      {aba === 'grade' ? <Grade aoAbrir={(c) => mudar('nota', c)} /> : null}
      {aba === 'carga' ? <MontarCarga /> : null}
      {aba === 'nfcompra' ? <NfCompra /> : null}
      {aba === 'cadastro' ? <Cadastro /> : null}

      <GavetaNota chave={nota} aoFechar={() => mudar('nota', null)} aoAgendar={definirAgendar} />
      <GavetaAgendar chaves={agendar} aoFechar={() => definirAgendar([])} />
    </div>
  );
}
